import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScriptEditor, ChapterForm, ScriptChatPanel, ScriptChatPanelRef, CreateEpisodeDialog, CreateEpisodeConfig } from '../components/projectScripts';
import { ProjectLayout, type NavItem } from '../components/projects';
import { useAutoGeneration } from '../hooks/useAutoGeneration';
import { projectScriptApi } from '../services/user/projectScript.api';
import { episodeScriptApi } from '../services/user/episodeScript.api';
import { useToast } from '../stores/toastStore';
import { useGlobalLoading } from '../stores/globalLoadingStore';
import {
  IProjectScript,
  CreateProjectScriptRequest,
  UpdateProjectScriptRequest,
  TOTAL_GENERATION_STEPS
} from '@packages/global';

export default function ProjectScriptPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation(['project', 'common']);
  const { showLoading, hideLoading } = useGlobalLoading();

  const [scripts, setScripts] = useState<IProjectScript[]>([]);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [showChapterForm, setShowChapterForm] = useState(false);
  const [editingChapter, setEditingChapter] = useState<IProjectScript | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  const [showChatPanel, setShowChatPanel] = useState(false);

  // 创建剧集相关状态
  const [showCreateEpisodeDialog, setShowCreateEpisodeDialog] = useState(false);
  const [isCreatingEpisode, setIsCreatingEpisode] = useState(false);

  // 自动生成状态 Hook
  const { state: autoGenState } = useAutoGeneration(projectId!);

  const chatPanelRef = useRef<ScriptChatPanelRef>(null);
  const hasTriggeredGenerate = useRef(false);

  // 从 URL 参数获取选中的章节和是否需要生成
  const urlChapterId = searchParams.get('chapterId');
  const shouldGenerate = searchParams.get('generate') === 'true';
  const outlineTitle = searchParams.get('outlineTitle');

  // 保存初始 URL 参数，避免被清除后丢失
  const initialParamsRef = useRef<{
    chapterId: string | null;
    generate: boolean;
    outlineTitle: string | null;
  } | null>(null);

  // 首次加载时保存 URL 参数
  useEffect(() => {
    if (urlChapterId && !initialParamsRef.current) {
      initialParamsRef.current = {
        chapterId: urlChapterId,
        generate: shouldGenerate,
        outlineTitle: outlineTitle
      };
    }
  }, [urlChapterId, shouldGenerate, outlineTitle]);

  useEffect(() => {
    if (projectId) {
      loadScripts();
    }
  }, [projectId]);

  // 处理 URL 参数中的章节选择和自动生成
  useEffect(() => {
    // 使用保存的参数或当前 URL 参数
    const params = initialParamsRef.current || {
      chapterId: urlChapterId,
      generate: shouldGenerate,
      outlineTitle: outlineTitle
    };

    if (!params.chapterId || scripts.length === 0 || hasTriggeredGenerate.current) {
      return;
    }

    const chapter = scripts.find(s => s.id === params.chapterId);
    if (!chapter) {
      return;
    }

    // 设置选中状态和显示面板
    setSelectedChapterId(params.chapterId);
    setShowChatPanel(true);

    // 如果需要自动生成
    if (params.generate) {
      hasTriggeredGenerate.current = true;

      // 清除 URL 参数，避免刷新页面重复触发
      setSearchParams({}, { replace: true });

      // 延迟触发，确保组件已挂载
      setTimeout(() => {
        const prompt = params.outlineTitle
          ? t('project:aiPrompt.generateFromOutline', { outlineTitle: params.outlineTitle, chapterTitle: chapter.title })
          : t('project:aiPrompt.generateForChapter', { chapterTitle: chapter.title });
        chatPanelRef.current?.triggerGenerate(prompt);
      }, 500);
    }
  }, [scripts, setSearchParams]);

  const loadScripts = useCallback(async () => {
    if (!projectId) return;
    try {
      setScriptsLoading(true);
      const scriptsList = await projectScriptApi.list(projectId);
      setScripts(scriptsList);
    } catch (error) {
      console.error('加载剧本章节失败:', error);
    } finally {
      setScriptsLoading(false);
    }
  }, [projectId]);

  const handleAddChapter = () => {
    setEditingChapter(null);
    setShowChapterForm(true);
  };

  const handleEditChapter = (chapter: IProjectScript) => {
    setEditingChapter(chapter);
    setShowChapterForm(true);
  };

  const handleSelectChapter = (chapter: IProjectScript) => {
    setSelectedChapterId(chapter.id);
    // 选择章节时显示聊天面板
    setShowChatPanel(true);
  };

  const handleSaveChapter = async (chapter: IProjectScript) => {
    const updateData: UpdateProjectScriptRequest = {
      title: chapter.title,
      content: chapter.content
    };
    await projectScriptApi.update(chapter.id, updateData);
    setScripts(prev => prev.map(s => s.id === chapter.id ? { ...s, ...updateData } : s));
  };

  const handleSubmitChapter = async (title: string) => {
    if (!projectId) return;
    try {
      if (editingChapter) {
        // 编辑模式：更新标题
        await projectScriptApi.update(editingChapter.id, { title });
        setScripts(prev => prev.map(s => s.id === editingChapter.id ? { ...s, title } : s));
        setShowChapterForm(false);
        toast.success(t('project:toast.chapterUpdated'));
      } else {
        // 新增模式
        const nextChapterNumber = scripts.length > 0
          ? Math.max(...scripts.map(s => s.chapterNumber)) + 1
          : 1;

        const createData: CreateProjectScriptRequest = {
          projectId,
          chapterNumber: nextChapterNumber,
          title,
          content: ''
        };
        const newChapter = await projectScriptApi.create(createData);
        setShowChapterForm(false);
        setScripts(prev => [...prev, newChapter]);
        setSelectedChapterId(newChapter.id);
        toast.success(t('project:toast.chapterCreated'));
      }
    } catch (error) {
      console.error('保存章节失败:', error);
      toast.error(editingChapter ? t('project:toast.updateFailed') : t('project:toast.createFailed'));
      throw error;
    }
  };

  const handleDeleteChapter = async (chapter: IProjectScript) => {
    if (!confirm(t('project:chapter.deleteConfirm', { title: chapter.title }))) return;
    try {
      await projectScriptApi.delete(chapter.id);
      setScripts(prev => prev.filter(s => s.id !== chapter.id));
      if (selectedChapterId === chapter.id) {
        setSelectedChapterId(null);
        setShowChatPanel(false);
      }
      toast.success(t('project:toast.chapterDeleted'));
    } catch (error) {
      console.error('删除章节失败:', error);
      toast.error(t('project:toast.deleteFailed'));
    }
  };

  const handleReorderChapters = async (reorderedChapters: IProjectScript[]) => {
    if (!projectId) return;

    // 乐观更新
    setScripts(reorderedChapters);

    try {
      await projectScriptApi.reorder({
        projectId,
        orders: reorderedChapters.map(ch => ({
          id: ch.id,
          sortOrder: ch.sortOrder || ch.chapterNumber,
          chapterNumber: ch.chapterNumber
        }))
      });
    } catch (error) {
      console.error('更新排序失败:', error);
      toast.error(t('project:toast.chapterReorderFailed'));
      // 回滚
      loadScripts();
    }
  };

  // 应用 AI 生成的内容到编辑器
  const handleApplyContent = (content: string, title?: string) => {
    if (!selectedChapterId) return;
    const chapter = scripts.find(s => s.id === selectedChapterId);
    if (!chapter) return;

    const updatedChapter = {
      ...chapter,
      content,
      ...(title && { title })
    };

    // 更新本地状态
    setScripts(prev => prev.map(s => s.id === selectedChapterId ? updatedChapter : s));

    // 保存到服务器
    handleSaveChapter(updatedChapter);
  };

  // 打开创建剧集对话框
  const handleOpenCreateEpisodeDialog = (chapter?: IProjectScript) => {
    // 如果传入了 chapter，使用它；否则使用当前选中的章节
    const targetChapterId = chapter?.id || selectedChapterId;
    if (!targetChapterId) return;
    setSelectedChapterId(targetChapterId);
    setShowCreateEpisodeDialog(true);
  };

  // 创建剧集
  const handleCreateEpisode = async (config: CreateEpisodeConfig) => {
    if (!selectedChapterId || !projectId) return;

    const selectedScript = scripts.find(s => s.id === selectedChapterId);
    if (!selectedScript) return;

    try {
      setIsCreatingEpisode(true);
      setShowCreateEpisodeDialog(false);
      showLoading(t('project:toast.episodeCreating'));

      // 调用 createEpisode API，传递 projectScriptId
      const result = await episodeScriptApi.createEpisode({
        prompt: config.prompt,
        projectId,
        projectScriptId: selectedChapterId,  // 传递章节ID，后端自动设置为 PROJECT_SCRIPT 模式
        imageModelId: config.text2imageModelId,
        llmModelId: config.llmModelId,
        aspectRatio: config.aspectRatio,
        targetDuration: config.targetDuration ?? undefined,
        maxShotDuration: config.maxShotDuration ?? undefined,
      });

      toast.success(t('project:toast.episodeCreated'));
      navigate(`/episode/${result.episodeId}/script-generation`);

    } catch (error: any) {
      const message = error.response?.data?.message || error.message || t('project:toast.episodeCreateFailed');
      toast.error(message);
      console.error('[CreateEpisode] 创建失败:', error);
    } finally {
      setIsCreatingEpisode(false);
      hideLoading();
    }
  };

  const selectedChapter = scripts.find(s => s.id === selectedChapterId);

  const handleNavItemChange = (item: NavItem) => {
    if (!projectId) return;

    // 设定相关的导航，跳转到 settings 页面并传递 nav 参数
    const navType = item.type === 'setting' ? item.settingType : item.type;
    navigate(`/project/${projectId}/settings?nav=${navType}`, { replace: true });
  };

  // 构建自动生成状态（传递给 ProjectLayout）
  const autoGenStatus = (autoGenState.isActive || autoGenState.completedSteps.length > 0) ? {
    isActive: autoGenState.isActive,
    currentStep: autoGenState.currentStep,
    completedSteps: autoGenState.completedSteps,
    failedStep: autoGenState.failedStep,
    totalSteps: TOTAL_GENERATION_STEPS,
    novelFileUrl: autoGenState.novelFileUrl
  } : undefined;

  return (
    <ProjectLayout activeTab="settings" activeNavItem={{ type: 'script' }} onNavItemChange={handleNavItemChange} autoGenStatus={autoGenStatus}>
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Main Content */}
        <section className={`flex-1 flex flex-col p-6 min-h-0 overflow-hidden transition-all ${showChatPanel ? 'pr-3' : ''}`}>
          <ScriptEditor
            projectId={projectId!}
            chapters={scripts}
            loading={scriptsLoading}
            selectedId={selectedChapterId}
            onAddChapter={handleAddChapter}
            onEditChapter={handleEditChapter}
            onSelectChapter={handleSelectChapter}
            onSaveChapter={handleSaveChapter}
            onDeleteChapter={handleDeleteChapter}
            onReorderChapters={handleReorderChapters}
            onCreateEpisode={handleOpenCreateEpisodeDialog}
            isCreatingEpisode={isCreatingEpisode}
          />
        </section>

        {/* Right Panel - Chat */}
        {showChatPanel && selectedChapter && (
          <aside className="w-[380px] p-4 pl-3 flex-shrink-0 min-h-0 overflow-hidden flex flex-col">
            {/* Chat Panel */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <ScriptChatPanel
                ref={chatPanelRef}
                projectId={projectId!}
                chapterId={selectedChapter.id}
                chapterTitle={selectedChapter.title}
                onApplyContent={handleApplyContent}
              />
            </div>
          </aside>
        )}
      </div>

      <ChapterForm
        isOpen={showChapterForm}
        onClose={() => setShowChapterForm(false)}
        onSubmit={handleSubmitChapter}
        editingChapter={editingChapter}
      />

      {/* 创建剧集配置对话框 */}
      {selectedChapter && (
        <CreateEpisodeDialog
          isOpen={showCreateEpisodeDialog}
          onClose={() => setShowCreateEpisodeDialog(false)}
          onConfirm={handleCreateEpisode}
          isProcessing={isCreatingEpisode}
          chapterTitle={selectedChapter.title}
        />
      )}
    </ProjectLayout>
  );
}
